import { Block } from '../classes/Block';
import { VideoSettings } from '../components/VideoSettings';
import { getApiUrl } from './api';
import { ApiBlock } from './apiTypes';
import { sanitizeFileNameSegment } from './stringSanitization';

export type ExportFormat = 'png' | 'tiff' | 'pdf';

export interface ExportConfig {
  format: ExportFormat;
  mimeType: string;
  fileExtension: string;
  overrideColors?: boolean; // For PDF: force black text and white background
}

export const EXPORT_CONFIGS: Record<ExportFormat, ExportConfig> = {
  png: {
    format: 'png',
    mimeType: 'image/png',
    fileExtension: 'png',
  },
  tiff: {
    format: 'tiff',
    mimeType: 'image/tiff',
    fileExtension: 'tiff',
  },
  pdf: {
    format: 'pdf',
    mimeType: 'application/pdf',
    fileExtension: 'pdf',
    overrideColors: true,
  },
};

export function transformBlockToApiFormat(
  block: Block,
  columnDefs: any[],
  overrideColors: boolean = false
): ApiBlock {
  const rows: string[][] = [];
  if (block.dataRows && block.dataRows.length > 0) {
    for (const dataRow of block.dataRows) {
      const rowValues: string[] = [];
      for (const colDef of columnDefs) {
        let value: string = String(dataRow[colDef.field] || '');

        // Special handling for logo blocks: ensure col2 is JSON encoded with scale key
        if (value && block.isLogoBlock && colDef.field === 'col2') {
          try {
            // If value is already a JSON string, do nothing
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _ = JSON.parse(value);
          } catch {
            const scaleValue: number = parseFloat(value);
            value = JSON.stringify({ scale: isNaN(scaleValue) ? 100 : scaleValue });
          }
        }
        rowValues.push(value);
      }
      rows.push([...rowValues]);
    }
  }

  return {
    name: block.name,
    properties: block.toRenderApiProperties(overrideColors),
    rows: rows,
  };
}

export interface ExportOptions {
  projectId: string;
  projectName: string;
  blocks: Block[];
  columnDefs: any[];
  videoSettings: VideoSettings;
  config: ExportConfig;
  transparent?: boolean;
}

export async function exportToFormat(options: ExportOptions): Promise<void> {
  const { projectId, projectName, blocks, columnDefs, videoSettings, config, transparent = false } = options;

  const apiBlocks: ApiBlock[] = blocks.map((block) =>
    transformBlockToApiFormat(block, columnDefs, config.overrideColors ?? false)
  );

  const payload = {
    video_settings: {
      background_color: config.overrideColors ? '#FFFFFF' : videoSettings.background_color,
      fps_num: videoSettings.fps_num,
      resolution: videoSettings.resolution,
      transparent: videoSettings.transparent,
      video_time: videoSettings.video_time,
      aspect_ratio: videoSettings.aspect_ratio,
      fonts: JSON.stringify(videoSettings.fonts),
      logos: JSON.stringify(videoSettings.logos),
    },
    data: apiBlocks,
    transparent,
  };

  const response = await fetch(`${getApiUrl()}/export-png?projectId=${projectId}&format=${config.format}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to export ${config.format.toUpperCase()}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const blob = new Blob([arrayBuffer], { type: config.mimeType });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  const now: Date = new Date();
  const padding = (n: number): string => String(n).padStart(2, '0');
  const ts: string = `${now.getFullYear()}-${padding(now.getMonth() + 1)}-${padding(now.getDate())}_${padding(now.getHours())}-${padding(now.getMinutes())}`;
  const safeName: string = sanitizeFileNameSegment(projectName, { fallback: 'export', lowercase: false });
  link.download = `Fintitles_${safeName}_${ts}.${config.fileExtension}`;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

